import { useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import "./ExportModal.css";

export type ExportPeriodType = "all" | "year" | "custom";

export interface ExportPeriod {
	type: ExportPeriodType;
	year?: number;
	startDate?: string;
	endDate?: string;
}

interface ExportModalProps {
	/** Callback when export is confirmed */
	onExport: (period: ExportPeriod) => void;
	/** Callback to close the modal */
	onClose: () => void;
	/** Whether export is in progress */
	isExporting?: boolean;
	/** Total number of rows to export */
	totalRows?: number;
}

/**
 * ExportModal - Modal for configuring data export options.
 * Allows users to select export period (all data, specific year, or custom date range).
 */
export function ExportModal({
	onExport,
	onClose,
	isExporting = false,
	totalRows,
}: ExportModalProps) {
	const [periodType, setPeriodType] = useState<ExportPeriodType>("all");
	const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");

	// Generate year options (last 10 years)
	const yearOptions = useMemo(() => {
		const currentYear = new Date().getFullYear();
		const years: number[] = [];
		for (let i = 0; i <= 10; i++) {
			years.push(currentYear - i);
		}
		return years;
	}, []);

	// Handle export confirmation
	const handleExport = useCallback(() => {
		const period: ExportPeriod = { type: periodType };

		if (periodType === "year") {
			period.year = selectedYear;
		} else if (periodType === "custom") {
			period.startDate = startDate;
			period.endDate = endDate;
		}

		onExport(period);
	}, [periodType, selectedYear, startDate, endDate, onExport]);

	// Check if export is valid
	const isExportValid = useMemo(() => {
		if (periodType === "all") return true;
		if (periodType === "year") return selectedYear > 0;
		if (periodType === "custom") return startDate && endDate && startDate <= endDate;
		return false;
	}, [periodType, selectedYear, startDate, endDate]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	// Handle backdrop click
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget && !isExporting) {
				onClose();
			}
		},
		[onClose, isExporting]
	);

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !isExporting) {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [onClose, isExporting]);

	return createPortal(
		<div className="exportModal-overlay" onClick={handleBackdropClick}>
			<div className="exportModal">
				{/* Header */}
				<div className="exportModal-header">
					<h2 className="exportModal-title">Export Data</h2>
					<button
						type="button"
						className="exportModal-closeBtn"
						onClick={onClose}
						disabled={isExporting}
						aria-label="Close"
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
							<path
								d="M1 1L13 13M1 13L13 1"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
					</button>
				</div>

				{/* Content */}
				<div className="exportModal-content">
					{/* Info message */}
					{totalRows !== undefined && (
						<div className="exportModal-info">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
								<path d="M8 7v4M8 5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
							<span>
								{totalRows.toLocaleString()} row{totalRows !== 1 ? "s" : ""} will be exported based on current filters and visible columns.
							</span>
						</div>
					)}

					{/* Period Type Selection - Hidden for now */}
					{false && (
						<>
							<div className="exportModal-section">
								<label className="exportModal-label">Export Period</label>
								<div className="exportModal-radioGroup">
									<label className="exportModal-radio">
										<input
											type="radio"
											name="periodType"
											value="all"
											checked={periodType === "all"}
											onChange={() => setPeriodType("all")}
											disabled={isExporting}
										/>
										<span className="exportModal-radioMark" />
										<span className="exportModal-radioLabel">All data (no date filter)</span>
									</label>
									<label className="exportModal-radio">
										<input
											type="radio"
											name="periodType"
											value="year"
											checked={periodType === "year"}
											onChange={() => setPeriodType("year")}
											disabled={isExporting}
										/>
										<span className="exportModal-radioMark" />
										<span className="exportModal-radioLabel">Specific year</span>
									</label>
									<label className="exportModal-radio">
										<input
											type="radio"
											name="periodType"
											value="custom"
											checked={periodType === "custom"}
											onChange={() => setPeriodType("custom")}
											disabled={isExporting}
										/>
										<span className="exportModal-radioMark" />
										<span className="exportModal-radioLabel">Custom date range</span>
									</label>
								</div>
							</div>

							{/* Year Selector */}
							{periodType === "year" && (
								<div className="exportModal-section">
									<label className="exportModal-label" htmlFor="export-year">
										Select Year
									</label>
									<select
										id="export-year"
										className="exportModal-select"
										value={selectedYear}
										onChange={(e) => setSelectedYear(Number(e.target.value))}
										disabled={isExporting}
									>
										{yearOptions.map((year) => (
											<option key={year} value={year}>
												{year}
											</option>
										))}
									</select>
								</div>
							)}

							{/* Custom Date Range */}
							{periodType === "custom" && (
								<div className="exportModal-section">
									<div className="exportModal-dateRange">
										<div className="exportModal-dateField">
											<label className="exportModal-label" htmlFor="export-start-date">
												Start Date
											</label>
											<input
												id="export-start-date"
												type="date"
												className="exportModal-input"
												value={startDate}
												onChange={(e) => setStartDate(e.target.value)}
												disabled={isExporting}
											/>
										</div>
										<div className="exportModal-dateField">
											<label className="exportModal-label" htmlFor="export-end-date">
												End Date
											</label>
											<input
												id="export-end-date"
												type="date"
												className="exportModal-input"
												value={endDate}
												onChange={(e) => setEndDate(e.target.value)}
												disabled={isExporting}
											/>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</div>

				{/* Footer */}
				<div className="exportModal-footer">
					<button
						type="button"
						className="exportModal-btn exportModal-btn--cancel"
						onClick={onClose}
						disabled={isExporting}
					>
						Cancel
					</button>
					<button
						type="button"
						className={`exportModal-btn exportModal-btn--export ${isExportValid && !isExporting ? "exportModal-btn--export-active" : ""}`}
						onClick={handleExport}
						disabled={!isExportValid || isExporting}
					>
						{isExporting ? (
							<>
								<span className="exportModal-spinner" />
								Exporting...
							</>
						) : (
							<>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path
										d="M2 10v3a1 1 0 001 1h10a1 1 0 001-1v-3M8 2v8M5 7l3 3 3-3"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								Export to Excel
							</>
						)}
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
}

export default ExportModal;

